import { useRef, useState } from 'react'

import { cap } from '../../State'
import { translations } from '../../translations'
import { Button } from '../Common/Button'
import { Card } from '../Common/Card'
import { Flex } from '../Common/Flex'
import { JsonViewer } from '../Common/JsonViewer'
import { Modal } from '../Common/Modal'
import { Text } from '../Common/Text'
import { AudioControl } from './AudioControl'
import { Composer } from './Composer'
const language = cap.language.state || 'IT'
const t = translations[language]

const dateOptionsShort = {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: 'numeric',
  minute: 'numeric'
}

const ChatWrapper = ({ children }) => (
  <Card style={{ width: '100%' }}>
    <Flex fw style={{ justifyContent: 'space-between', height: '95%' }}>
      {children}
    </Flex>
  </Card>
)

const Writer = ({
  sendMessage,
  composerStyle,
  noAttachments,
  noraDraft,
  composerVariant,
  composerPlaceholder,
  composerSendAriaLabel
}) => {
  const [currentMessage, setCurrentMessage] = useState('')
  const [sending, setSending] = useState(false)
  const [files, setFiles] = useState([])
  const refBtn = useRef(null)

  const onSend = async () => {
    if (!currentMessage?.length && !files?.length) return // controllo che non sia vuoto il message, al fine di non fare invio inutilmente.
    setSending(true)
    setCurrentMessage('')
    const attachments = await Promise.all(
      files.map(async (file) => {
        const reader = new FileReader()
        reader.readAsDataURL(file)
        return new Promise((resolve) => {
          reader.onload = () => resolve(reader.result)
        })
      })
    )
    setFiles([])
    sendMessage(currentMessage, attachments)
    setSending(false)
  }

  return (
    <Flex fw className="nora-writer">
      <input
        type="file"
        id="file"
        name="file"
        onChange={(e) => setFiles([...files, ...e.target.files])}
        style={{ display: 'none' }}
        ref={refBtn}
        multiple
      />
      {files.length > 0 && (
        <div className="nora-attach-list" role="list" aria-label={t.fileAllegati}>
          {files.map((file, index) => (
            <div key={`${file.name}-${index}`} className="nora-attach-chip" role="listitem">
              <i className="pi pi-file nora-attach-chip__icon" aria-hidden="true" />
              <span className="nora-attach-chip__name" title={file.name}>
                {file.name}
              </span>
              <button
                type="button"
                className="nora-attach-chip__remove"
                onClick={() => setFiles(files.filter((_, i) => i !== index))}
                aria-label={`${t.rimuovi} ${file.name}`}
              >
                <i className="pi pi-times" aria-hidden="true" />
              </button>
            </div>
          ))}
        </div>
      )}
      <div
        className={['nora-writer__bar', composerVariant === 'gigi' ? 'nora-writer__bar--gigi' : '']
          .filter(Boolean)
          .join(' ')}
      >
        {!noAttachments && (
          <button
            type="button"
            className="nora-attach-btn"
            onClick={() => refBtn.current.click()}
            aria-label={t.allegaFile}
          >
            <i className="pi pi-paperclip" aria-hidden="true" />
          </button>
        )}
        <Composer
          update={setCurrentMessage}
          send={onSend}
          currentMessage={currentMessage}
          sending={sending}
          composerStyle={composerStyle}
          variant={composerVariant}
          placeholder={composerPlaceholder}
          sendAriaLabel={composerSendAriaLabel}
        />
      </div>
      {noraDraft && (
        <>
          <Flex fw row jb style={{ marginTop: 20 }}>
            <Text bold value={t.messaggioBozzaNora} />
            <Button
              label={t.copia}
              icon="copy"
              onClick={() => {
                setCurrentMessage(noraDraft)
              }}
            />
          </Flex>
          <Text value={noraDraft} style={{ marginTop: 5 }} />
        </>
      )}
    </Flex>
  )
}

const sanitizeMsg = (msg) =>
  msg
    ?.replace(/<br\s*\/?>/g, '\n')
    ?.replace(/<(?!img\s)[a-zA-Z][^>]*>/g, '')
    ?.replace(/(<img\s+[^>]+>)/gi, '\n$1\n')
    ?.replace(/<\/p>/gi, '\n')
    ?.replace(/<\/a>/gi, '')
    ?.split('\n')
    ?.map((p) => p?.trim())
    ?.filter(Boolean)

const URL_REGEX = /(https?:\/\/[^\s<>"{}|\\^`[\]]+)/g

/**
 * Verifica che un URL sia sicuro (protocollo http/https, no javascript:, data:, vbscript:)
 * @param {string} url - URL da verificare
 * @returns {boolean} true se l'URL è sicuro da usare come href
 */
const isSafeUrl = (url) => {
  try {
    const parsed = new URL(url)
    return ['http:', 'https:'].includes(parsed.protocol)
  } catch {
    return false
  }
}

const linkifyText = (text) => {
  if (!text || typeof text !== 'string') return text
  const parts = text.split(URL_REGEX)
  if (parts.length === 1) return text
  return parts.map((part, i) => {
    if (!/^https?:\/\//.test(part)) return part
    const trailing = part.match(/[),.;:!?]+$/)?.[0] || ''
    const cleanUrl = trailing ? part.slice(0, -trailing.length) : part
    if (!isSafeUrl(cleanUrl)) return part
    return (
      <span key={i}>
        <a href={cleanUrl} target="_blank" rel="noopener noreferrer" className="nora-chat-link">
          {cleanUrl}
        </a>
        {trailing}
      </span>
    )
  })
}

const ChatMessages = ({ messages = [], jsonVisualized }) => {
  if (!messages?.length)
    return (
      <Flex
        fw
        style={{
          width: '100%',
          flex: 1,
          justifyContent: 'center',
          gap: 10,
          color: 'var(--ink-400)'
        }}
      >
        <i className="pi pi-comments" style={{ fontSize: 28, opacity: 0.5 }} />
        <Text value={t.nessunMessaggio} style={{ fontSize: 13, color: 'var(--ink-400)' }} />
      </Flex>
    )
  return (
    <Flex fw js style={{ width: '100%', overflowY: 'auto', maxHeight: '90%', marginBottom: 20 }}>
      {messages.map((message, index) => (
        <ChatMessage key={index} message={message} jsonVisualized={jsonVisualized} />
      ))}
    </Flex>
  )
}

const ChatMessage = ({ message, jsonVisualized = true }) => {
  const { jsonPayload, message: messageText = '', user, createdAt } = message
  const [showImg, setShowImg] = useState(false)
  const [media, setMedia] = useState(null)

  const renderMessage = () => {
    // check if message is HTML generic (es. preventivo email)
    if (messageText?.includes('<html')) {
      // Anteprima: usiamo l'HTML completo così com'è (senza DOMPurify).
      // Il contenuto è generato dal backend (createPDFQuotesRioFlow), non da input utente;
      // DOMPurify con documenti completi tende a rimuovere <head> e <style>, quindi
      // la preview resterebbe senza stili. Per questo caso scriviamo l'HTML raw nella nuova finestra.
      const fullHtml = messageText
      return (
        <>
          <Text value={t.clickToPreviewMessage} />
          <Button
            style={{ marginTop: 10, marginBottom: 10 }}
            icon="show"
            label={t.previewMessage}
            onClick={() => {
              const previewWindow = window.open('', '_blank')
              previewWindow.document.write(fullHtml)
              previewWindow.document.close()
            }}
          />
        </>
      )
    }

    return sanitizeMsg(messageText)?.map((p = '\n', index) => {
      if (p.includes('<img')) {
        const mediaUrl = p.match(/<img\s+[^>]*\bsrc="(https?:\/\/[^">]+)"/i)?.[1]
        // Tag img incompleto o senza URL valido: mostra il paragrafo come testo (rimuovendo il frammento img)
        if (!mediaUrl) {
          const textOnly = p
            .replace(/<img\s+[^>]*>/gi, '')
            .replace(/<img[^>]*$/i, '')
            .trim()
          return (
            <Text
              key={index}
              value={linkifyText(textOnly || p)}
              size={15}
              style={{ wordBreak: 'break-word' }}
            />
          )
        }
        return (
          <img
            key={index}
            src={mediaUrl}
            style={{ width: '100%', marginTop: 10, cursor: 'pointer' }}
            onClick={() => {
              setMedia({ url: mediaUrl, type: 'image' })
              setShowImg(true)
            }}
          />
        )
      }
      return (
        <Text key={index} value={linkifyText(p)} size={15} style={{ wordBreak: 'break-word' }} />
      )
    })
  }

  return (
    <Flex fw ae={user !== 'Nora'} as={user === 'Nora'}>
      <div
        className={`nora-chat-bubble ${user === 'Nora' ? 'nora-chat-bubble--nora' : 'nora-chat-bubble--user'}`}
      >
        <Flex fw as row jb>
          <Text value={user} style={{ fontSize: 12 }} />
          <Text
            value={createdAt?.toLocaleString('it-IT', dateOptionsShort)}
            style={{ fontSize: 12 }}
          />
        </Flex>
        <hr style={{ width: '100%', margin: '5px 0' }} />
        {renderMessage()}
        <Flex fw js row style={{ marginTop: 10 }}>
          {message.attachments
            ?.filter(({ type, inBodyEmail }) => type.includes('image') && !inBodyEmail)
            .map((media, index) => (
              <Button
                icon="image"
                label={`${t.gfImmagine} ${index + 1}`}
                onClick={() => {
                  setMedia(media)
                  setShowImg(true)
                }}
                key={index}
                style={{ marginRight: 10 }}
              />
            ))}
          {message.attachments
            ?.filter(({ type }) => type.includes('audio'))
            .map((media, index) => (
              <AudioControl
                key={index}
                url={media.url}
                label={`${t.gfAudio} ${index + 1}`}
                style={{ marginRight: 10 }}
              />
            ))}
        </Flex>
        <Modal
          style={{ maxWidth: '80%', maxHeight: '80%', borderRadius: 10 }}
          contentStyle={{ padding: 0, borderRadius: 10 }}
          visible={showImg}
          onHide={() => setShowImg(false)}
        >
          <Button
            icon="close"
            round
            onClick={() => setShowImg(false)}
            style={{ position: 'absolute', top: 10, right: 10 }}
          />
          {media?.type?.includes('image') && <img src={media?.url} style={{ width: '100%' }} />}
          {media?.type?.includes('audio') && (
            <Flex style={{ minWidth: 500, height: 100 }}>
              <audio
                controls
                src={media?.url}
                style={{ width: '80%' }}
                ref={(audio) => {
                  audio?.setAttribute('controlsList', 'nodownload')
                }}
              />
            </Flex>
          )}
        </Modal>
        {jsonVisualized && !!jsonPayload && <VisualizeJSONPayload jsonPayload={jsonPayload} />}
      </div>
    </Flex>
  )
}

const VisualizeJSONPayload = ({ jsonPayload }) => {
  const [openJSON, setOpenJSON] = useState(window?._env_?.ENVIRONMENT === 'development')
  return (
    <>
      {!openJSON && <Button label={t.leggiJson} onClick={() => setOpenJSON(true)} />}
      {openJSON && (
        <>
          <JsonViewer name={t.risultatoElaborazione} src={jsonPayload} />
          <Button label={t.chiudi} onClick={() => setOpenJSON(false)} />
        </>
      )}
    </>
  )
}

export { ChatMessage, ChatMessages, ChatWrapper, Writer }
